
import * as THREE from "../../../libs/three.js/build/three.module.js";
import {DigitizationCategories, DigitizationTools} from "../ToolRegistry.js";
import {createFeature} from "../Feature.js";
import {FeatureVisual} from "../FeatureVisual.js";
import {loadRoiPoints, DEFAULT_POINT_BUDGET} from "../volume/RoiPointLoader.js";
import {computeStations, selectProfilePoints, formatStationName, computeCorridorBounds, computePathDirection, computeRotatedCorridorBounds} from "../geometry/crossSectionGeometry.js";
import {CrossSectionMarkersVisual} from "../crosssection/CrossSectionMarkersVisual.js";
import {renderTopViewSnapshot} from "../crosssection/TopViewSnapshot.js";
import {promptCrossSectionSettings} from "../crosssection/CrossSectionSettingsPrompt.js";
import {showCrossSectionReport, showCrossSectionErrorPanel} from "../crosssection/CrossSectionReportPanel.js";
import {addStationOverlays} from "../../modules/ProfileOverlays/ProfileOverlays.js";

import {t, T} from "../i18n/index.js";
// New top-level "Rapporter" section - this tool produces a printable
// documentation report (task's own framing: "profilrapportgenerator"), a
// distinct purpose from plain digitizing/measuring, so it gets its own
// sidebar category rather than being folded into "Specialiseret
// registrering" (which already covers a different kind of tool).
DigitizationCategories.register({id: "reports", name: T("Rapporter"), order: 4});

// Fixed profile band width (task section 4: "en fast værdi i denne første
// version af værktøjet og skal ikke indtastes af brugeren") - the
// longitudinal tolerance (along the path) used to gather points for each
// individual profile; NOT the same size as the user-entered profile length
// (the cross-path extent) - see crossSectionGeometry.js's own doc comment.
const PROFILE_BAND_WIDTH = 0.25;

// Minimum width:height ratio for the top-view overview render - see its
// own use below for why.
const OVERVIEW_MIN_ASPECT = 1.8;

// "Tværprofiler" - digitize an arbitrary open polyline through the point
// cloud, then automatically place perpendicular cross-section profiles
// along it at a regular interval, extract point-cloud points within a
// fixed 0.25 m band around each, and present them as a top-view overview +
// a grid of 2D profile charts, exportable to PDF.
//
// Custom capture (same reason as "Areal og volumen"/volumeTool.js: an
// async step - here, an interstitial settings dialog PLUS a ROI point load -
// must run between "capture finished" and "feature committed", which the
// generic vertex-capture flow has no hook for). Orchestration below
// mirrors runVolumeCapture() in volumeTool.js closely: capture -> (here:
// settings dialog) -> deterministic ROI point extraction (RoiPointLoader.js,
// the SAME mechanism volumeTool.js already uses, not a new/parallel one) ->
// pure, synchronous profile computation (see geometry/crossSectionGeometry.js -
// no Worker needed here, unlike volumeTool.js's own Delaunay-based surface
// reconstruction: this tool's own math is simple filtering/projection, fast
// enough to stay synchronous even for realistic point counts/profile
// counts) -> commit exactly one Feature (the digitized path) -> show the
// report.
export const CrossSectionTool = {
	id: "digitize_cross_sections",
	name: T("Tværprofilrapport"),
	category: "reports",
	kind: "measurement",
	toolbarGroup: "report",
	toolbarOrder: 10,
	icon: "cross_section.svg",
	resultGeometryType: "LineString",
	featureType: T("Tværprofiler"),

	tooltip: T("Digitalisér et forløb og generér automatisk en serie tværprofiler med top-view-oversigt og PDF-rapport."),

	customCapture(dm, tool){
		runCrossSectionCapture(dm, tool);
	},
};

DigitizationTools.register(CrossSectionTool);

// Open-polyline capture loop - left-click adds a vertex (previewed live),
// right-click finishes (requires >= 2 vertices), ESC/cancel_insertions
// discards everything. Mirrors volumeTool.js's own capturePolygon() shape
// exactly, just open (no closing edge) and with a 2-vertex minimum instead
// of 3 (a cross-section path can be a single straight segment).
function captureCrossSectionPath(dm, tool, onDone){
	const domElement = dm.viewer.renderer.domElement;
	const visual = new FeatureVisual("LineString");
	dm.scene.add(visual);

	let finished = false;

	const cleanup = () => {
		domElement.removeEventListener("mouseup", onMouseUp, false);
		dm.viewer.removeEventListener("cancel_insertions", onCancel);
		dm.clearRubberBand();
	};

	const onCancel = () => {
		if(finished) return;
		finished = true;
		cleanup();
		dm.scene.remove(visual);
		onDone(null);
	};

	const onMouseUp = (e) => {
		if(finished || dm.isPaused() || dm._consumeCopyClickGuard()) return;

		if(e.button === THREE.MOUSE.LEFT){
			const location = dm._pickPlacementPosition(e);
			if(!location) return;
			visual.appendAtEnd(location);
			dm._markOperationStarted();
			dm.syncRubberBandToVisual(visual);
			dm._showInstruction(tool.name, t("Venstreklik for at tilføje flere punkter. Højreklik for at afslutte (mindst 2 punkter, nu {0}). ESC annullerer.", visual.points.length));
			return;
		}

		if(e.button === THREE.MOUSE.RIGHT){
			if(visual.points.length < 2) return; // not enough yet - ignore, keep capturing
			finished = true;
			cleanup();
			onDone(visual);
		}
	};

	domElement.addEventListener("mouseup", onMouseUp, false);
	dm.viewer.addEventListener("cancel_insertions", onCancel);

	dm._showInstruction(tool.name, t("Digitalisér forløbet gennem punktskyen. Venstreklik tilføjer punkter, højreklik afslutter (mindst 2 punkter). ESC annullerer."));
}

// Given an already-digitized path + its Tværprofiler settings, loads the
// shared ROI corridor and computes every station's own profile points -
// the data half of the report pipeline. Shared between the original
// capture flow (runCrossSectionCapture() below) AND the "Vis rapport"
// context-menu action (VectorEditingMenu.js's showCrossSectionReportForFeature()) -
// the ONE place this logic lives, per the task's own "genbrug eksisterende
// rapportfunktionalitet, ikke en parallel generator" requirement. Never
// shows any UI itself (no error panel, no instruction banner) - returns
// `{stations}` on success or `{error: message}` on failure, so each caller
// can decide how (or whether, e.g. a cancelled capture stays silent) to
// surface a failure, without this function needing to know about either
// caller's own cancellation/UI concerns.
export async function loadCrossSectionStations(pointcloud, {points, interval, profileLength, band, viewer = null}){
	const stations = computeStations(points, interval);
	for(const station of stations) station.name = formatStationName(station.index);

	// ONE shared ROI load for every station at once (not one per profile -
	// see crossSectionGeometry.js's own computeCorridorBounds() doc comment
	// for why a simple padded bounding box is enough: RoiPointLoader only
	// ever uses the bounding box of whatever polygon it's given anyway).
	const bounds = computeCorridorBounds(points, profileLength / 2, band);
	const avgZ = (points[0][2] + points[points.length - 1][2]) / 2;
	const corridorPolygon = [
		{x: bounds.minX, y: bounds.minY, z: avgZ},
		{x: bounds.maxX, y: bounds.minY, z: avgZ},
		{x: bounds.maxX, y: bounds.maxY, z: avgZ},
		{x: bounds.minX, y: bounds.maxY, z: avgZ},
	];

	let roi;
	try{
		roi = await loadRoiPoints(pointcloud, corridorPolygon, {pointBudget: DEFAULT_POINT_BUDGET});
	}catch(err){
		return {error: t("Kunne ikke indlæse punktskydata: {0}", (err && err.message) || err)};
	}
	if(roi.rawPointCount === 0){
		return {error: t("Ingen punktskydata blev fundet langs det digitaliserede forløb.")};
	}

	for(const station of stations){
		// roi.colors (see RoiPointLoader.js) lets each profile point keep its
		// own real point-cloud colour (user-requested) rather than a single
		// flat plotting colour
		station.profilePoints = selectProfilePoints(roi.positions, station, profileLength, band, roi.colors);
	}

	// IFC-snit og terraenlinje (baggrundskortet) i hver profil, hvis de er taendt
	if(viewer){
		try{
			await addStationOverlays(viewer, stations, profileLength);
		}catch(err){
			console.warn("Tvaerprofil-overlays fejlede:", err);
		}
	}

	return {stations};
}

// The render half of the pipeline: given already-computed stations, frames/
// renders the top-view overview and shows the report modal. Also shared
// between the original capture flow and "Vis rapport" - see
// loadCrossSectionStations()'s own doc comment above.
export function renderAndShowCrossSectionReport(dm, {points, profileLength, stations, featureName, interval, band, dataSource, coordinateSystem}){
	// Top-view is framed/oriented in the path's OWN direction (user-
	// requested: the digitized vector should always read reasonably
	// horizontally, left-to-right) - a SEPARATE, rotated framing from
	// computeCorridorBounds() (which stays world-axis-aligned, since that's
	// all RoiPointLoader actually needs for its own bounding-box query).
	// OVERVIEW_MIN_ASPECT keeps the rendered image a predictable, wide
	// "banner" shape (never taller than it is wide by more than this ratio)
	// at the report's own full page width, so a short path with a large
	// profile length can't produce an overview tall enough to dominate the
	// front page - see computeRotatedCorridorBounds()'s own doc comment.
	const pathDirection = computePathDirection(points);
	const rotatedBounds = computeRotatedCorridorBounds(points, profileLength / 2, band, pathDirection, OVERVIEW_MIN_ASPECT);
	const overviewCanvas = renderTopViewSnapshot(dm.viewer, {
		rotatedBounds, polylinePoints: points, stations, profileLength,
	});

	showCrossSectionReport({
		stations,
		overviewCanvas,
		meta: {featureName, interval, profileLength, band, dataSource, coordinateSystem},
	});
}

async function runCrossSectionCapture(dm, tool){
	const visual = await new Promise((resolve) => captureCrossSectionPath(dm, tool, resolve));
	if(!visual){
		dm._hideToolHint();
		if(dm.activeTool === tool) dm.stopInsertion();
		return;
	}

	const abortDiscarded = () => {
		dm.scene.remove(visual);
		dm._hideToolHint();
		if(dm.activeTool === tool) dm.stopInsertion();
	};

	// Settings dialog (task section 3) - shown right after capture finishes,
	// before anything is committed. Cancelling here discards the just-
	// captured path entirely, same as ESC during capture itself.
	const settings = await promptCrossSectionSettings(dm);
	if(!settings){
		abortDiscarded();
		return;
	}
	const {interval, profileLength} = settings;

	let cancelled = false;
	const onCancelDuringWork = () => { cancelled = true; };
	dm.viewer.addEventListener("cancel_insertions", onCancelDuringWork);

	const abortDuringWork = () => {
		dm.viewer.removeEventListener("cancel_insertions", onCancelDuringWork);
		abortDiscarded();
	};

	const pointcloud = dm.viewer.scene.pointclouds[0];
	if(!pointcloud){
		abortDuringWork();
		showCrossSectionErrorPanel(t("Ingen punktsky er indlæst."), dm);
		return;
	}

	const points = visual.rawCoordinates(); // [[x,y,z], ...]

	dm._showInstruction(tool.name, t("Indlæser punktskydata…"));
	const result = await loadCrossSectionStations(pointcloud, {points, interval, profileLength, band: PROFILE_BAND_WIDTH, viewer: dm.viewer});
	// cancellation takes priority over a load error even if both are true -
	// a cancelled capture stays silent, matching the original behaviour
	// (see loadCrossSectionStations()'s own doc comment on why it never
	// shows UI itself)
	if(cancelled){ abortDuringWork(); return; }
	if(result.error){
		abortDuringWork();
		showCrossSectionErrorPanel(result.error, dm);
		return;
	}
	const {stations} = result;

	dm.viewer.removeEventListener("cancel_insertions", onCancelDuringWork);
	if(cancelled){ abortDiscarded(); return; }

	// Commit the feature (task section 13: the digitized path is a real,
	// integrated part of Registreringer - selectable, deletable, editable -
	// same as every other tool's own result).
	const feature = createFeature({
		geometryType: "LineString",
		coordinates: visual.toCoordinates(),
		category: tool.category,
		tool: tool.id,
		featureType: tool.featureType,
		name: `${tool.name} ${dm.featureManager.features.length + 1}`,
		attributes: {
			crossSection: {interval, profileLength, band: PROFILE_BAND_WIDTH, stationCount: stations.length},
		},
	});

	visual.setInfo(feature.properties.name, feature.properties.description);
	// vertex coordinate labels would otherwise compete visually with the
	// "P01"/"P02"... markers already placed along the same path
	visual.setVertexLabelsSuppressed(true);

	dm._wireFeatureLifecycle(feature, visual);

	// 3D markers (task section 5: "vis hvor hvert enkelt profil ligger i
	// punktskyen") - same purely-decorative-child + userData.auxiliaryVisual
	// hook HeightAuxiliaryVisual.js already established, so labelsVisible/
	// per-frame screen-space scaling come for free with no
	// Tværprofiler-specific code in FeatureVisual/DigitizationModule.
	const markers = new CrossSectionMarkersVisual();
	markers.setStations(stations, profileLength);
	visual.add(markers);
	visual.userData.auxiliaryVisual = markers;

	dm.dispatchEvent({type: "feature_created", feature, visual});

	dm._hideToolHint();
	if(dm.activeTool === tool) dm.stopInsertion();

	renderAndShowCrossSectionReport(dm, {
		points, profileLength, stations,
		featureName: feature.properties.name,
		interval, band: PROFILE_BAND_WIDTH,
		dataSource: pointcloud.name || null,
		// `pointcloud.projection` is only ever populated when the source
		// data's own metadata declared one (see PointCloudOctree.js's own
		// "TODO read projection from file instead") - shown only when
		// genuinely known, never guessed/fabricated
		coordinateSystem: pointcloud.projection || null,
	});
}
